import * as S from './styles';

import PanelOptionMixin from '@/components/Dashboard/common/WidgetSettingPanel/options/mixin';
import SecondPanelContent from '@/components/Dashboard/common/WidgetSettingPanel/options/filterWidgetOptions/linkedWidgets/secondPanelContent';
import { NoItemMessage } from '@/components/Dashboard/common/WidgetSettingPanel/options/filterWidgetOptions/styles';

import LinkIcon from '@svgIcons/link_chain.svg';

import Dialog from '@/components/Common/Dialog';

import { mapGetters, mapState } from 'vuex';
import { isValueExist } from '@/utils/utils';
import { isExcludedForLinkedWidget } from '@/components/Dashboard/common/WidgetSettingPanel/utils';
import { getIcon } from '@/components/Dashboard/common/WidgetSettingPanel/options/filterWidgetOptions/linkedWidgets/utils';

export default {
    mixins: [PanelOptionMixin],
    name: 'LinkedWidgets',
    props: {
        linkedWidgetIds: {
            type: Array,
            default: [],
        },
        handleUpdateLinkedWidgetIds: {
            type: Function,
            default: () => {},
        },
    },
    data() {
        return {
            active: false,
            secondPanelOutsideClickActive: true,
            showDialog: false,
            pendingCloseProceed: null,
            originalWidgetIds: [],
        };
    },
    watch: {
        panelVisibility(visible) {
            if (visible) {
                this.originalWidgetIds = this.linkedWidgetIds;
            }

            this.$emit('outsideClick:active', !visible);
        },
    },
    computed: {
        ...mapState('newDashboard', ['dimensions', 'currentWidgetTitlesById']),
        ...mapGetters('newDashboard', ['currentWidgetsById', 'viewLayout']),
        panelVisibility() {
            return this.isSecondPanelOpen;
        },
        isLinkedWidgetIdsChanged() {
            const { originalWidgetIds, linkedWidgetIds } = this;
            const originalIdsSet = new Set(originalWidgetIds);
            const currentIdsSet = new Set(linkedWidgetIds);
            return !R.equals(originalIdsSet, currentIdsSet);
        },
        widgets() {
            const { viewLayout, currentWidgetsById } = this;

            const layout = R.propOr([], 'layout', viewLayout);

            const getSortedWidgets = R.compose(
                R.map((l) => currentWidgetsById?.[l.i]),
                R.sortWith([R.ascend(R.prop('y')), R.ascend(R.prop('x'))])
            );

            const getFilteredWidgets = R.filter(
                R.complement(isExcludedForLinkedWidget)
            );

            return R.compose(getFilteredWidgets, getSortedWidgets)(layout);
        },
        linkedWidgets() {
            const { widgets, getIsLinkedWidget } = this;
            return R.filter(
                R.compose(getIsLinkedWidget, R.prop('id')),
                widgets
            );
        },
    },
    methods: {
        closePanel() {
            this.active = false;
            this.closeSecondPanel();
        },
        renderSecondPanelContent() {
            const {
                widgets,
                linkedWidgetIds,
                handleUpdateLinkedWidgetIds,
                isLinkedWidgetIdsChanged,
            } = this;

            const secondPanelContentProps = {
                props: {
                    linkedWidgetIds,
                    handleUpdateLinkedWidgetIds,
                    widgets,
                    handleCancel: () => {
                        if (isLinkedWidgetIdsChanged) {
                            this.openDialog();
                        }
                        this.closePanel();
                    },
                    handleSubmit: this.closePanel,
                },
            };

            return <SecondPanelContent {...secondPanelContentProps} />;
        },
        renderLinkedWidgets() {
            const { linkedWidgets, getWidgetTitle } = this;
            if (!isValueExist(linkedWidgets)) {
                return (
                    <NoItemMessage>
                        {this.$t(
                            'NEW_DASHBOARD.linked_widgets_panel.no_widget_selected'
                        )}
                    </NoItemMessage>
                );
            }

            return (
                <S.LinkedWidgetsList>
                    {linkedWidgets.map((widget) => {
                        const { id } = widget;
                        const Icon = getIcon(widget);
                        const title = getWidgetTitle(id);
                        return (
                            <S.LinkedWidgetsItem key={id}>
                                {Icon && (
                                    <Icon
                                        v-view-box
                                        width="16"
                                        height="16"
                                        style={{
                                            flexShrink: 0,
                                        }}
                                        fill="#2A333D"
                                    />
                                )}
                                <S.LinkedWidgetsItemTitle class="adriel-ellipsis">
                                    {title}
                                </S.LinkedWidgetsItemTitle>
                            </S.LinkedWidgetsItem>
                        );
                    })}
                </S.LinkedWidgetsList>
            );
        },
        openDialog() {
            this.showDialog = true;
        },
        closeDialog() {
            this.showDialog = false;
        },
        resetLinkedWidgets() {
            const { originalWidgetIds, handleUpdateLinkedWidgetIds } = this;

            handleUpdateLinkedWidgetIds(originalWidgetIds);
        },
        hasUnsavedOpenPanel() {
            return this.active && this.isLinkedWidgetIdsChanged;
        },
        requestCloseWithConfirm(onProceed) {
            this.pendingCloseProceed = onProceed;
            this.openDialog();
        },
        resolvePendingClose() {
            const { pendingCloseProceed } = this;
            if (!pendingCloseProceed) return;

            this.pendingCloseProceed = null;
            pendingCloseProceed();
        },
        handleDialogConfirm() {
            const { closeDialog, closePanel } = this;
            closeDialog();
            closePanel();
            this.resolvePendingClose();
        },
        handleDialogCancel() {
            const {
                isLinkedWidgetIdsChanged,
                resetLinkedWidgets,
                closeDialog,
                closePanel,
            } = this;
            if (isLinkedWidgetIdsChanged) {
                resetLinkedWidgets();
            }

            closeDialog();
            closePanel();
            this.resolvePendingClose();
        },
        getDialogProps() {
            const { showDialog, handleDialogConfirm, handleDialogCancel } =
                this;
            const dialogProps = {
                props: {
                    active: showDialog,
                    backdrop: true,
                    child: (
                        <div>{this.$t('NEW_DASHBOARD.ask_apply_change')}</div>
                    ),
                    emitCancelOnClickOutside: true,
                },
                on: {
                    confirm: handleDialogConfirm,
                    cancel: handleDialogCancel,
                    'update:active': (val) => {
                        this.showDialog = val;
                    },
                },
            };
            return dialogProps;
        },
        getIsLinkedWidget(widgetId) {
            const { linkedWidgetIds } = this;
            return linkedWidgetIds.includes(widgetId);
        },
        getWidgetTitle(widgetId) {
            return this.currentWidgetTitlesById?.[widgetId];
        },
    },
    render() {
        const {
            wrapOptions,
            secondPanelOutsideClickActive,
            isLinkedWidgetIdsChanged,
            openDialog,
            renderSecondPanel,
            renderSecondPanelContent,
            renderLinkedWidgets,
            getDialogProps,
        } = this;

        const dialogProps = getDialogProps();

        return (
            <div>
                {renderSecondPanel({
                    title: this.$t('NEW_DASHBOARD.linked_widgets_panel.title'),
                    content: renderSecondPanelContent(),
                    closeOutside: secondPanelOutsideClickActive,
                    onClose: isLinkedWidgetIdsChanged
                        ? openDialog
                        : () => (this.active = false),
                })}
                {wrapOptions({
                    children: renderLinkedWidgets(),
                    title: this.$t('NEW_DASHBOARD.linked_widgets'),
                    icon: <LinkIcon class="metric-icon" />,
                    hasEditBtn: true,
                    onClick: () => (this.active = true),
                    disableClick: this.active,
                })}
                <portal to="panel_modal">
                    <Dialog {...dialogProps} />
                </portal>
            </div>
        );
    },
};
